import React, { useState, useEffect } from "react";
import {
  Button,
  Group,
  Notification,
  Stack,
  Modal,
  Loader,
  Text,
  TextInput,
  Input,
  Paper,
  Grid,
  Divider,
  Badge,
  ActionIcon,
} from "@mantine/core";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { useForm } from "@mantine/form";
import { IconCheck, IconAlertCircle, IconEdit, IconPlus, IconReceipt, IconTrash } from "@tabler/icons-react";
import { db } from "../../firebase";

export interface BillingInfoProps {
  companyId: string;
}

export interface BillingDoc {
  billingEmails: string[];
  countryOfResidence: string;
  addressLine1: string;
  billingName: string;
  taxNumber: string;
  addressStateProvince: string;
  addressPostalCode: string;
  companyRegistrationNumber: string;
  addressCity: string;
  addressLine2?: string;
  companyId?: string;
}

export interface BillingFormValues {
  billingEmails: string[];
  countryOfResidence: string;
  addressLine1: string;
  billingName: string;
  taxNumber: string;
  addressStateProvince: string;
  addressPostalCode: string;
  companyRegistrationNumber: string;
  addressCity: string;
  addressLine2: string;
}

export const BillingInfo = ({ companyId }: BillingInfoProps) => {
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [emailList, setEmailList] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchedBillingInfo, setFetchedBillingInfo] = useState(false);
  const [billingDoc, setBillingDoc] = useState<BillingDoc | undefined>();
  const [opened, setOpened] = useState(false);

  const form = useForm<BillingFormValues>({
    initialValues: {
      billingEmails: [],
      countryOfResidence: "",
      addressLine1: "",
      billingName: "",
      taxNumber: "",
      addressStateProvince: "",
      addressPostalCode: "",
      companyRegistrationNumber: "",
      addressCity: "",
      addressLine2: "",
    },
  });

  const addInput = () => {
    setEmailList([...emailList, ""]);
  };

  const removeInput = (index: number) => {
    const updated = emailList.filter((_, idx) => idx !== index);
    setEmailList(updated);
  };

  useEffect(() => {
    if (!fetchedBillingInfo && companyId) {
      const fetchBillingInfo = async () => {
        setFetchedBillingInfo(true);
        setLoading(true);
        try {
          const reference = doc(db, "billingInfo", companyId);
          const documentSnapshot = await getDoc(reference);
          if (documentSnapshot.exists()) {
            const data = documentSnapshot.data() as BillingDoc;
            setBillingDoc(data);
            Object.keys(data).forEach((k) => {
              const key = k as keyof BillingFormValues;
              if (key in form.values) {
                form.setFieldValue(key, (data[key as keyof BillingDoc] as any) ?? "");
              }
            });
            setEmailList(data.billingEmails || []);
          } else {
            setBillingDoc(undefined);
          }
        } catch (err) {
          console.error("Error fetching billing info:", err);
        } finally {
          setLoading(false);
        }
      };
      fetchBillingInfo();
    }
  }, [companyId, fetchedBillingInfo, form]);

  const saveConfig = async () => {
    setLoading(true);
    const validEmails = emailList.map((e) => e.trim()).filter(Boolean);
    const payload = {
      billingEmails: validEmails,
      billingName: form.values.billingName,
      addressLine1: form.values.addressLine1,
      addressLine2: form.values.addressLine2,
      addressCity: form.values.addressCity,
      addressStateProvince: form.values.addressStateProvince,
      addressPostalCode: form.values.addressPostalCode,
      countryOfResidence: form.values.countryOfResidence,
      companyRegistrationNumber: form.values.companyRegistrationNumber,
      taxNumber: form.values.taxNumber,
      registeredTax: !!form.values.companyRegistrationNumber && !!form.values.taxNumber,
      companyId,
    };
    const reference = doc(db, "billingInfo", companyId);

    try {
      await setDoc(reference, payload, { merge: true });
      setBillingDoc(payload as BillingDoc);
      setMessage("Billing information updated successfully.");
      setOpened(false);
    } catch (err) {
      console.error("Failed to save billing info:", err);
      setErrorMessage("Could not save billing details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && !billingDoc ? (
        <Loader style={{ margin: "2em auto", display: "block" }} />
      ) : billingDoc ? (
        <Paper p="lg" radius="md" withBorder style={{ border: "1px solid #e9ecef" }}>
          <Group position="apart" mb="md" align="flex-start">
            <div>
              <Text size="md" weight={600} color="dark">
                Billing & Invoicing Profile
              </Text>
              <Text size="xs" color="dimmed" mt={2}>
                Official tax details, registered invoicing address, and automated payment receipt notifications
              </Text>
            </div>
            <Button
              size="sm"
              variant="outline"
              color="cap-blue"
              leftIcon={<IconEdit size={14} />}
              onClick={() => setOpened(true)}
            >
              Edit Details
            </Button>
          </Group>

          <Divider my="sm" />

          <Grid gutter="xl" mt="xs">
            <Grid.Col span={6}>
              <Stack spacing="sm">
                <div>
                  <Text size="xs" weight={600} color="dimmed">
                    Legal / Billing Name
                  </Text>
                  <Text size="sm" weight={600} color="dark">
                    {billingDoc.billingName || "—"}
                  </Text>
                </div>
                <div>
                  <Text size="xs" weight={600} color="dimmed">
                    Company Tax / VAT Number
                  </Text>
                  <Text size="sm" weight={600} color="dark">
                    {billingDoc.taxNumber || "—"}
                  </Text>
                </div>
                <div>
                  <Text size="xs" weight={600} color="dimmed">
                    Company Registration Number
                  </Text>
                  <Text size="sm" weight={600} color="dark">
                    {billingDoc.companyRegistrationNumber || "—"}
                  </Text>
                </div>
                <div>
                  <Text size="xs" weight={600} color="dimmed">
                    Billing Account ID
                  </Text>
                  <Text size="xs" style={{ fontFamily: "monospace" }}>
                    {companyId}
                  </Text>
                </div>
              </Stack>
            </Grid.Col>

            <Grid.Col span={6}>
              <Stack spacing="sm">
                <div>
                  <Text size="xs" weight={600} color="dimmed">
                    Invoicing Address
                  </Text>
                  <Text size="sm">{billingDoc.addressLine1 || "—"}</Text>
                  {billingDoc.addressLine2 && <Text size="sm">{billingDoc.addressLine2}</Text>}
                  <Text size="sm">
                    {[billingDoc.addressCity, billingDoc.addressStateProvince, billingDoc.addressPostalCode]
                      .filter(Boolean)
                      .join(", ")}
                  </Text>
                  {billingDoc.countryOfResidence && <Text size="sm">{billingDoc.countryOfResidence}</Text>}
                </div>
                <div>
                  <Text size="xs" weight={600} color="dimmed" mb={4}>
                    Invoice Notification Recipients
                  </Text>
                  {billingDoc.billingEmails && billingDoc.billingEmails.length > 0 ? (
                    <Group spacing="xs">
                      {billingDoc.billingEmails.map((em, idx) => (
                        <Badge key={idx} size="sm" variant="light" color="cap-blue">
                          {em}
                        </Badge>
                      ))}
                    </Group>
                  ) : (
                    <Text size="xs" color="dimmed">
                      No billing emails registered
                    </Text>
                  )}
                </div>
              </Stack>
            </Grid.Col>
          </Grid>
        </Paper>
      ) : (
        <Paper
          p="xl"
          radius="md"
          withBorder
          style={{ border: "1px dashed #ced4da", textAlign: "center", backgroundColor: "#f8f9fa" }}
        >
          <Stack align="center" spacing="xs">
            <IconReceipt size={36} color="#adb5bd" />
            <Text size="md" weight={600} color="dark">
              No billing profile configured yet
            </Text>
            <Text size="xs" color="dimmed" style={{ maxWidth: 420 }}>
              Set up your company's tax number, registered business address, and automated invoice delivery recipients.
            </Text>
            <Button
              size="sm"
              color="cap-navy"
              leftIcon={<IconPlus size={14} />}
              onClick={() => setOpened(true)}
              mt="xs"
            >
              Add Billing Details
            </Button>
          </Stack>
        </Paper>
      )}

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title="Billing & Invoicing Information"
        size="md"
      >
        <form onSubmit={form.onSubmit(saveConfig)}>
          <Stack spacing="sm">
            <TextInput
              required
              size="sm"
              label="Company Name (Billing)"
              placeholder="e.g. Acme Logistics Corp"
              {...form.getInputProps("billingName")}
            />
            <TextInput
              required
              size="sm"
              label="Address Line 1"
              placeholder="Street address or P.O. Box"
              {...form.getInputProps("addressLine1")}
            />
            <TextInput
              size="sm"
              label="Address Line 2 (Optional)"
              placeholder="Suite, unit, or building"
              {...form.getInputProps("addressLine2")}
            />
            <Grid gutter="sm">
              <Grid.Col span={6}>
                <TextInput
                  required
                  size="sm"
                  label="City"
                  {...form.getInputProps("addressCity")}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  required
                  size="sm"
                  label="State / Province"
                  {...form.getInputProps("addressStateProvince")}
                />
              </Grid.Col>
            </Grid>
            <Grid gutter="sm">
              <Grid.Col span={6}>
                <TextInput
                  required
                  size="sm"
                  label="Country"
                  {...form.getInputProps("countryOfResidence")}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  required
                  size="sm"
                  label="Postal Code"
                  {...form.getInputProps("addressPostalCode")}
                />
              </Grid.Col>
            </Grid>
            <Grid gutter="sm">
              <Grid.Col span={6}>
                <TextInput
                  size="sm"
                  label="Tax / VAT Number"
                  placeholder="e.g. 4123456789"
                  {...form.getInputProps("taxNumber")}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  size="sm"
                  label="Registration Number"
                  placeholder="e.g. 2024/123456/07"
                  {...form.getInputProps("companyRegistrationNumber")}
                />
              </Grid.Col>
            </Grid>

            <div>
              <Text size="sm" weight={500} mb={4}>
                Invoice Delivery Emails
              </Text>
              <Stack spacing="xs">
                {emailList.map((email, i) => (
                  <Group key={i} spacing="xs" noWrap>
                    <Input
                      value={email}
                      size="sm"
                      placeholder="accounts@company.com"
                      style={{ flex: 1 }}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const newList = [...emailList];
                        newList[i] = e.target.value;
                        setEmailList(newList);
                      }}
                    />
                    <ActionIcon
                      color="red"
                      variant="light"
                      size="md"
                      style={{ minHeight: 36, minWidth: 36 }}
                      onClick={() => removeInput(i)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                ))}
              </Stack>
              <Button
                variant="subtle"
                color="cap-blue"
                onClick={addInput}
                size="sm"
                style={{ minHeight: 36 }}
                leftIcon={<IconPlus size={14} />}
                mt="xs"
              >
                Add another email
              </Button>
            </div>

            <Divider my="sm" />

            <Group position="right" spacing="sm">
              <Button variant="default" color="gray" size="sm" onClick={() => setOpened(false)}>
                Cancel
              </Button>
              <Button size="sm" color="cap-navy" type="submit" loading={loading}>
                Save Details
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {message && (
        <Notification
          icon={<IconCheck size={16} />}
          color="green"
          title="Updated"
          style={{ position: "fixed", bottom: "24px", right: "24px", zIndex: 1000 }}
          onClose={() => setMessage("")}
        >
          {message}
        </Notification>
      )}
      {errorMessage && (
        <Notification
          icon={<IconAlertCircle size={16} />}
          color="red"
          title="Error"
          style={{ position: "fixed", bottom: "24px", right: "24px", zIndex: 1000 }}
          onClose={() => setErrorMessage("")}
        >
          {errorMessage}
        </Notification>
      )}
    </>
  );
};

export default BillingInfo;

